import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { useParams, Outlet, useSearchParams, useLocation } from "react-router-dom";
import api from "@/lib/api";
import DiamondFooter from "@/components/DiamondFooter";
import AnimatedBackground from "@/components/AnimatedBackground";
import AnimatedFX from "@/components/AnimatedFX";
import { fileUrl } from "@/lib/api";
import { THEMES as _THEMES, getTheme as _getTheme } from "@/lib/themes";
import { Loader2, Menu } from "lucide-react";

const PublicCtx = createContext(null);
export const usePublic = () => useContext(PublicCtx);

export const THEMES = _THEMES;
export const getTheme = _getTheme;

const FLAGS = { it: "🇮🇹", en: "🇬🇧", fr: "🇫🇷", de: "🇩🇪", es: "🇪🇸", pt: "🇵🇹", hr: "🇭🇷", hu: "🇭🇺", nl: "🇳🇱", pl: "🇵🇱", ro: "🇷🇴", ru: "🇷🇺", sk: "🇸🇰" };

const PublicLayout = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isHome = location.pathname.replace(/\/+$/, "") === `/m/${slug}`;
  const noTranslate = slug === "pizzeriaazzura";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState("it");
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    api.get(`/public/${slug}`).then((res) => {
      setData(res.data);
      setLoading(false);
      const wanted = searchParams.get("lang");
      const avail = res.data?.settings?.languages || ["it"];
      if (wanted && avail.includes(wanted)) setLang(wanted);
    }).catch(() => setLoading(false));
  }, [slug]);

  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "auto" }); }, [location.pathname]);

  // Solo per "pizzeriaazzura": blocca la traduzione automatica del browser (storpia i nomi dei piatti).
  useEffect(() => {
    if (slug !== "pizzeriaazzura") return;
    const docEl = document.documentElement;
    const prevLang = docEl.getAttribute("lang");
    const meta = document.createElement("meta");
    meta.setAttribute("name", "google");
    meta.setAttribute("content", "notranslate");
    document.head.appendChild(meta);
    return () => {
      if (prevLang === null) docEl.removeAttribute("lang");
      else docEl.setAttribute("lang", prevLang);
      if (meta.parentNode) meta.parentNode.removeChild(meta);
    };
  }, [slug]);

  // Allinea <html lang> alla lingua scelta dal cliente (e la aggiorna al cambio lingua).
  useEffect(() => {
    if (slug !== "pizzeriaazzura") return;
    document.documentElement.setAttribute("lang", lang);
  }, [slug, lang]);

  const theme = useMemo(() => {
    const t = getTheme(data?.settings);
    if (data?.settings?.background_path === "/backgrounds/white.jpg") {
      // Sfondo bianco: forza una palette chiara con testo scuro, leggibile anche sui temi scuri.
      return { ...t, dark: false, bg: "#FFFFFF", surface: "#F4F4F5", text: "#1A1A1A", muted: "#6B6B6B" };
    }
    return t;
  }, [data]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0B0B0C] text-[#C9A227] gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Caricamento…</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-[#0B0B0C] text-white">Menù non trovato.</div>;

  if (data.suspended) {
    const t = theme;
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6 relative overflow-hidden" style={{ backgroundColor: t.bg, color: t.text, fontFamily: t.body }} data-testid="menu-suspended">
        <AnimatedBackground base={t.bg} accent={t.accent} />
        <div className="relative z-10 max-w-md">
          {data.settings?.logo_path && (
            <div className="w-24 h-24 mx-auto flex items-center justify-center mb-6">
              <img src={fileUrl(data.settings.logo_path)} alt={data.restaurant?.name} className="w-full h-full object-contain" />
            </div>
          )}
          <h1 className="text-3xl md:text-4xl" style={{ fontFamily: t.heading, color: t.accent }}>{data.restaurant?.name}</h1>
          <div className="mx-auto mt-3 h-px w-16" style={{ background: `linear-gradient(90deg, transparent, ${t.accent}, transparent)` }} />
          <p className="mt-6 text-lg" style={{ fontFamily: t.heading, color: t.accentLight }}>Menù temporaneamente non disponibile</p>
          <p className="mt-3 text-sm" style={{ color: t.muted }}>Ci scusiamo per il disagio. Per informazioni e per consultare il menù, contatta direttamente il locale.</p>
        </div>
      </div>
    );
  }

  const { settings, restaurant } = data;
  const langs = settings.languages || ["it"];
  const isDark = !!theme.dark;

  return (
    <PublicCtx.Provider value={{ data, theme, lang, setLang, slug }}>
      <div className={`min-h-screen flex flex-col relative${noTranslate ? " notranslate" : ""}`} translate={noTranslate ? "no" : undefined} style={{ color: theme.text, fontFamily: theme.body, backgroundColor: settings.background_path ? "transparent" : theme.bg }}>
        {settings.background_path ? (
          <div className="fixed inset-0 -z-10" data-testid="public-background">
            <img src={fileUrl(settings.background_path)} alt="" className="w-full h-full object-cover" style={{ objectPosition: "center" }} />
            <div className="absolute inset-0" style={{ backgroundColor: `${theme.bg}80` }} />
          </div>
        ) : isDark ? <AnimatedBackground base={theme.bg} accent={theme.accent} /> : <div className="fixed inset-0 -z-10" style={{ backgroundColor: theme.bg }} />}

        {isHome && <AnimatedFX variant={settings.animated_bg} color={theme.accent} />}

        {/* Brand header */}
        <header className="relative z-30 pt-6 pb-4 px-5 flex flex-col items-center text-center">
          {langs.length > 1 && (
            <>
              {/* Desktop / tablet: bandiere in linea */}
              <div className="hidden sm:flex gap-2.5 mb-4" data-testid="lang-flags">
                {langs.map((l) => (
                  <button
                    key={l}
                    onClick={() => setLang(l)}
                    data-testid={`lang-${l}`}
                    aria-label={`Lingua ${l}`}
                    className="w-10 h-10 rounded-full flex items-center justify-center text-xl leading-none transition-transform hover:scale-110 active:scale-95"
                    style={{ backgroundColor: theme.surface, boxShadow: l === lang ? `0 0 0 2px ${theme.accent}` : "none", opacity: l === lang ? 1 : 0.5 }}
                  >
                    <span>{FLAGS[l] || l.toUpperCase()}</span>
                  </button>
                ))}
              </div>
              {/* Mobile: menù a tendina (hamburger) con le lingue */}
              <div className="sm:hidden relative mb-4" data-testid="lang-menu-mobile">
                <button
                  onClick={() => setLangOpen((o) => !o)}
                  data-testid="lang-menu-toggle"
                  aria-label="Cambia lingua"
                  className="flex items-center gap-2 rounded-full pl-2 pr-3 py-1.5 transition-transform active:scale-95"
                  style={{ backgroundColor: theme.surface, boxShadow: `0 0 0 1px ${theme.accent}55` }}
                >
                  <span className="w-7 h-7 rounded-full flex items-center justify-center text-lg leading-none">{FLAGS[lang] || lang.toUpperCase()}</span>
                  <Menu className="w-4 h-4" style={{ color: theme.accent }} />
                </button>
                {langOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setLangOpen(false)} />
                    <div className="absolute left-1/2 -translate-x-1/2 mt-2 z-20 w-[210px] rounded-2xl p-2 grid grid-cols-4 gap-1.5 shadow-xl" style={{ backgroundColor: theme.surface, border: `1px solid ${theme.accent}33` }} data-testid="lang-menu-dropdown">
                      {langs.map((l) => (
                        <button
                          key={l}
                          onClick={() => { setLang(l); setLangOpen(false); }}
                          data-testid={`lang-mobile-${l}`}
                          aria-label={`Lingua ${l}`}
                          className="w-10 h-10 rounded-full flex items-center justify-center text-xl leading-none transition-transform active:scale-95"
                          style={{ boxShadow: l === lang ? `0 0 0 2px ${theme.accent}` : "none", opacity: l === lang ? 1 : 0.6 }}
                        >
                          <span>{FLAGS[l] || l.toUpperCase()}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
          {settings.logo_path && (
            <div className="w-full flex items-center justify-center mb-4">
              <img src={fileUrl(settings.logo_path)} alt={restaurant.name} className="w-auto max-w-[300px] sm:max-w-[440px] max-h-56 sm:max-h-72 object-contain" data-testid="public-logo" />
            </div>
          )}
          {settings.show_name_in_home !== false && (
            <h1 className="text-3xl md:text-4xl font-medium tracking-wide" style={{ fontFamily: theme.heading, color: theme.accent }} data-testid="public-restaurant-name">{restaurant.name}</h1>
          )}
          <div className="mt-2 h-px w-16" style={{ background: `linear-gradient(90deg, transparent, ${theme.accent}, transparent)` }} />
        </header>

        <main className="flex-1 relative z-10 w-full max-w-2xl mx-auto px-4 pb-16">
          <Outlet />
        </main>

        <DiamondFooter />
      </div>
    </PublicCtx.Provider>
  );
};

export default PublicLayout;
